/**
 * NotificationBell — header inbox next to the EN/VI toggle. Polls /api/notifications every minute
 * (and on window focus) — the API writes a row for every web-push fan-out, so this is the same
 * feed as the phone's notifications, kept even for people who never enabled push.
 * Unread rows are bold with a dot; clicking one marks it read (cache patched first, so the badge
 * drops immediately) and opens the popup with the full text. Dropdown z-30 like the other header
 * dropdowns — below the mobile drawer backdrop (z-40), which must keep winning.
 */
import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useNavigate } from '@tanstack/react-router'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import { NOTIFICATION_KINDS, type NotificationKind, type NotificationListResponse, type NotificationRecord } from '@system/shared'
import { listNotifications, markAllNotificationsRead, markNotificationRead } from '../lib/api-client'
import { useLang, useT } from '../i18n/language-provider'
import { Icon } from './ui/icon'
import { KIND_ICON, kindLabelKey } from './notification-kind'
import { NotificationPopup } from './notification-popup'

export const NOTIFICATIONS_QUERY_KEY = ['notifications'] as const
const POLL_MS = 60_000

interface Props {
  /** False until the access profile resolved — the locked/expired screens must not poll. */
  enabled: boolean
}

export function NotificationBell({ enabled }: Props) {
  const t = useT()
  const navigate = useNavigate()
  const { locale } = useLang()
  const qc = useQueryClient()
  const [open, setOpen] = useState(false)
  const [active, setActive] = useState<NotificationRecord | null>(null)
  // Source filter: chips appear only for kinds present in the loaded page, so an inbox with
  // nothing but issues shows no chip row at all.
  const [filter, setFilter] = useState<NotificationKind | 'all'>('all')
  const ref = useRef<HTMLDivElement>(null)

  const { data } = useQuery({
    queryKey: NOTIFICATIONS_QUERY_KEY,
    queryFn: async () => {
      const res = await listNotifications()
      if (!res.success) throw new Error(res.error.message)
      return res.data
    },
    enabled,
    refetchInterval: POLL_MS,
    refetchOnWindowFocus: true,
    // Focus refetches are welcome but not on every alt-tab; and once the session dies there is
    // nothing to retry — `access` never flips `enabled` back.
    staleTime: 30_000,
    retry: false,
  })
  const items = data?.items ?? []
  const unread = data?.unread ?? 0
  const kindsPresent = useMemo(() => NOTIFICATION_KINDS.filter((k) => items.some((n) => n.kind === k)), [items])
  const shown = filter === 'all' ? items : items.filter((n) => n.kind === filter)
  // A poll can drop the selected kind off the page (and unmount its chip): fall back to "all"
  // rather than stranding the user on an empty list with no control to reset it.
  useEffect(() => {
    if (filter !== 'all' && !kindsPresent.includes(filter)) setFilter('all')
  }, [filter, kindsPresent])

  useEffect(() => {
    if (!open) return
    function onDoc(e: MouseEvent) { if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false) }
    function onKey(e: KeyboardEvent) { if (e.key === 'Escape') setOpen(false) }
    document.addEventListener('mousedown', onDoc)
    document.addEventListener('keydown', onKey)
    return () => {
      document.removeEventListener('mousedown', onDoc)
      document.removeEventListener('keydown', onKey)
    }
  }, [open])

  const timeFmt = useMemo(
    () => new Intl.DateTimeFormat(locale === 'vi' ? 'vi-VN' : 'en-US', { day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit' }),
    [locale],
  )
  const timeLabel = (iso: string) => timeFmt.format(new Date(iso))

  /**
   * Patch the cache in place: server order is kept, unread never goes below zero. A poll already
   * in flight is cancelled first, or its stale answer would put the badge back for a minute.
   */
  function patch(fn: (cur: NotificationListResponse) => NotificationListResponse) {
    qc.setQueryData<NotificationListResponse>(NOTIFICATIONS_QUERY_KEY, (cur) => (cur ? fn(cur) : cur))
    void qc.cancelQueries({ queryKey: NOTIFICATIONS_QUERY_KEY })
  }

  function settle(res: { success: boolean }) {
    if (!res.success) void qc.invalidateQueries({ queryKey: NOTIFICATIONS_QUERY_KEY })
  }

  function markRead(item: NotificationRecord) {
    if (item.readAt) return
    const readAt = new Date().toISOString()
    patch((cur) => ({
      items: cur.items.map((n) => (n.id === item.id ? { ...n, readAt } : n)),
      unread: Math.max(0, cur.unread - 1),
    }))
    void markNotificationRead(item.id).then(settle)
  }

  function openItem(item: NotificationRecord) {
    setOpen(false)
    setActive(item)
    markRead(item)
  }

  // Jump straight to the entry's page from the row (no popup): mark read, close the dropdown, go.
  function goToItem(item: NotificationRecord) {
    setOpen(false)
    markRead(item)
    void navigate({ to: item.url })
  }

  function readAll() {
    const readAt = new Date().toISOString()
    patch((cur) => ({ items: cur.items.map((n) => (n.readAt ? n : { ...n, readAt })), unread: 0 }))
    void markAllNotificationsRead().then(settle)
  }

  const closePopup = useCallback(() => setActive(null), [])

  return (
    <div ref={ref} className="relative">
      <button
        id="layout-notify-btn"
        type="button"
        onClick={() => setOpen((o) => !o)}
        aria-label={unread > 0 ? `${t('notify.title')} (${unread} ${t('notify.unread').toLowerCase()})` : t('notify.title')}
        aria-expanded={open}
        title={t('notify.title')}
        className="relative rounded p-1.5 text-muted hover:bg-hover hover:text-fg-soft"
      >
        <Icon name="bell" className="h-5 w-5" />
        {unread > 0 && (
          <span id="layout-notify-badge" className="absolute -right-0.5 -top-0.5 flex h-4 min-w-4 items-center justify-center rounded-full bg-destructive px-1 text-[10px] font-bold leading-none text-white">
            {unread > 99 ? '99+' : unread}
          </span>
        )}
      </button>

      {open && (
        // Phones: the bell sits ~100px from the right edge, so a right-anchored panel would run off
        // the left of a 320px screen; pin it to the viewport instead and let it span the width.
        <div id="layout-notify-dropdown" className="absolute right-0 top-full z-30 mt-1 w-80 overflow-hidden rounded-lg border border-border bg-surface shadow-xl max-md:fixed max-md:inset-x-4 max-md:top-16 max-md:mt-0 max-md:w-auto">
          <div className="flex items-center justify-between border-b border-border-soft px-3 py-2">
            <span className="text-sm font-semibold text-fg">{t('notify.title')}{unread > 0 && <span className="ml-1 text-xs font-normal text-muted">· {unread} {t('notify.unread').toLowerCase()}</span>}</span>
            {unread > 0 && (
              <button id="layout-notify-read-all-btn" type="button" onClick={readAll} className="text-xs text-primary hover:underline">{t('notify.markAllRead')}</button>
            )}
          </div>
          {kindsPresent.length > 1 && (
            <div id="layout-notify-filters" className="flex flex-wrap gap-1 border-b border-border-soft px-3 py-1.5">
              {(['all', ...kindsPresent] as const).map((k) => (
                <button
                  key={k}
                  id={`layout-notify-filter-${k}`}
                  type="button"
                  aria-pressed={filter === k}
                  onClick={() => setFilter(k)}
                  className={['rounded-full px-2 py-0.5 text-[11px]', filter === k ? 'bg-primary text-white' : 'bg-hover text-fg-soft hover:text-fg'].join(' ')}
                >
                  {k === 'all' ? t('notify.filterAll') : t(kindLabelKey(k))}
                </button>
              ))}
            </div>
          )}
          <ul className="max-h-96 overflow-y-auto">
            {shown.length === 0 && <li className="px-3 py-6 text-center text-sm text-muted">{t('notify.empty')}</li>}
            {shown.map((n) => {
              const isUnread = !n.readAt
              const hasLink = !!n.url && n.url !== '/'
              const openLabel = t(n.kind === 'issue' ? 'notify.viewIssue' : 'notify.open')
              return (
                <li key={n.id} className={['flex items-stretch border-b border-border-soft last:border-b-0', isUnread ? 'bg-primary-soft/40' : ''].join(' ')}>
                  <button
                    id={`layout-notify-item-${n.id}`}
                    type="button"
                    data-unread={isUnread ? 'true' : 'false'}
                    data-kind={n.kind}
                    onClick={() => openItem(n)}
                    className="flex min-w-0 flex-1 items-start gap-2 px-3 py-2 text-left hover:bg-hover"
                  >
                    <span className="relative mt-0.5 shrink-0" aria-hidden="true">
                      <Icon name={KIND_ICON[n.kind]} className={['h-4 w-4', isUnread ? 'text-primary' : 'text-faint'].join(' ')} />
                      {isUnread && <span className="absolute -right-1 -top-1 h-2 w-2 rounded-full bg-primary" />}
                    </span>
                    {isUnread && <span className="sr-only">{t('notify.new')}: </span>}
                    <span className="min-w-0 flex-1">
                      <span className={['block truncate text-sm', isUnread ? 'font-semibold text-fg' : 'text-fg-soft'].join(' ')}>{n.title}</span>
                      {n.body && <span className="block truncate text-xs text-muted">{n.body}</span>}
                      <span className="block text-[11px] text-faint">{timeLabel(n.createdAt)}</span>
                    </span>
                  </button>
                  {hasLink && (
                    <button
                      id={`layout-notify-open-${n.id}`}
                      type="button"
                      onClick={() => goToItem(n)}
                      title={openLabel}
                      aria-label={openLabel}
                      className="flex shrink-0 items-center border-l border-border-soft px-3 text-muted hover:bg-hover hover:text-primary"
                    >
                      <Icon name="external" className="h-4 w-4" />
                    </button>
                  )}
                </li>
              )
            })}
          </ul>
        </div>
      )}

      {active && <NotificationPopup item={active} onClose={closePopup} />}
    </div>
  )
}
